/**
 * Shared founder-facing Task title/outcome for Tasks home and Roadmap/Priority.
 * Mirrors server deriveTaskDisplayCopy, and keeps client schema-key short titles.
 */
import { taskShortTitle } from "./taskShortTitle";

function cleanText(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function shortenTitle(value, max = 64) {
  const text = cleanText(value);
  if (!text) return "Untitled Task";
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

function outcomeFromText(value, title) {
  const text = cleanText(value);
  if (!text) return title || "Untitled Task";
  const first = text.split(/(?<=[.!?])\s+/)[0] || text;
  if (first.length <= 120) return first;
  return `${first.slice(0, 119).trimEnd()}…`;
}

/**
 * Prefer schema-family short title, then product title, intent/goal, then request.
 * Mirrors server `taskDisplayCopy.ts` so Tasks home and Roadmap/Priority match.
 */
export function deriveTaskDisplayCopy(plan) {
  const explicitTitle = cleanText(plan?.title || "");
  const request = cleanText(plan?.featureRequestText || "");
  const productTitle = cleanText(plan?.productDocument?.content?.title || "");
  const productOutcome = cleanText(plan?.productDocument?.content?.outcome || "");
  const intentTitle = cleanText(plan?.intentContract?.title || plan?.planIntelligence?.goal || "");
  const intentSummary = cleanText(plan?.intentContract?.summary || "");

  const schemaTitle = (() => {
    const fromRequest = taskShortTitle(request, "");
    if (fromRequest === "Case-insensitive schema keys") return fromRequest;
    const fromProduct = taskShortTitle(productTitle, "");
    if (fromProduct === "Case-insensitive schema keys") return fromProduct;
    return null;
  })();

  const requestTitle = (() => {
    if (!request) return "Untitled Task";
    let clause = request.split(/(?<=[.!?])\s+/)[0] || request;
    clause = clause.replace(/^(make|ensure|improve|add|fix|update|implement|create)\s+/i, "");
    return shortenTitle(clause, 64);
  })();

  const title = explicitTitle
    ? shortenTitle(explicitTitle, 64)
    : schemaTitle
      ? schemaTitle
      : productTitle
        ? shortenTitle(productTitle, 64)
        : intentTitle
          ? shortenTitle(intentTitle, 64)
          : requestTitle;

  const outcome = productOutcome
    ? outcomeFromText(productOutcome, title)
    : intentSummary
      ? outcomeFromText(intentSummary, title)
      : outcomeFromText(request || title, title);

  return { title, outcome };
}
